'use client';
import React from 'react';
import { StockSymbol } from '@benzinga/session';
import { Quote } from '@benzinga/quotes-v3-manager';
import Hooks from '@benzinga/hooks';
import { useQuoteSubscription } from '@benzinga/quotes-v3-manager-hooks';
import styled from '@benzinga/themetron';
import { PerformanceColor } from '../components/Ticker';

interface OwnProps {
  symbol: StockSymbol;
  getSnapshotSize?(
    symbol: string | null,
    currentPrice: string | null,
    nominalChange: number | null,
    percentChange: number | null,
  ): number;
  reflow?(size: number): void;
}

type Props = OwnProps;

const formatNumber = (num: string | null) => {
  if (num) return Number(num).toLocaleString('en-US');
  else return num;
};

const TickerQuoteFormatter = styled.span`
  display: flex;
  flex-direction: row;
  flex-grow: 0;
  flex-shrink: 0;
  padding: 0 10px;
  flex-basis: 275px;
  justify-content: space-around;
`;

const MiniSnapshotV3: React.FC<Props> = React.memo(
  props => {
    const fields = React.useMemo(() => {
      return new Set(['symbol', 'price', 'change', 'changePercent']) as Set<keyof Quote>;
    }, []);

    const quote = useQuoteSubscription(props.symbol, fields);

    const memoizedQuoteData = React.useMemo(() => {
      if (!quote) return null;

      return {
        change: quote.change,
        changePercent: quote.changePercent,
        price: quote.price,
      };
    }, [quote]);

    const { getSnapshotSize, reflow, symbol } = props;

    Hooks.useEffectDidUpdate(
      (prevSymbol: typeof symbol, prevGetSnapshotSize: typeof getSnapshotSize, prevQuote: typeof memoizedQuoteData) => {
        if (!getSnapshotSize || !reflow || !prevGetSnapshotSize) {
          return;
        }

        const prevChange = prevQuote?.change ?? null;
        const prevCurrentPrice = prevQuote?.price?.toString() ?? null;
        const prevPercentChange = prevQuote?.changePercent ?? null;

        const change = memoizedQuoteData?.change ?? null;
        const currentPrice = memoizedQuoteData?.price?.toString() ?? null;
        const percentChange = memoizedQuoteData?.changePercent ?? null;

        if (
          prevSymbol === symbol &&
          prevChange === change &&
          prevPercentChange === percentChange &&
          prevCurrentPrice === currentPrice
        ) {
          return;
        }

        const prevSize = prevGetSnapshotSize(prevSymbol, prevCurrentPrice, prevChange, prevPercentChange);
        const size = getSnapshotSize(symbol, currentPrice, change, percentChange);

        if (prevSize !== size) {
          reflow(size);
        }
      },
      [symbol, getSnapshotSize, memoizedQuoteData],
    );

    const renderTicker = React.useCallback(() => {
      const { change, changePercent, price } = memoizedQuoteData ?? {};
      const currentPrice = formatNumber(price?.toString() ?? null);
      const nominalChange = change ?? null;
      const percentageChange = changePercent ?? null;

      return (
        <TickerQuoteFormatter key={symbol}>
          <span>{symbol}</span>
          <span className="price">{currentPrice}</span>
          <span className="nominalChange">
            <PerformanceColor decimalPlaces={3} hasArrow stat={nominalChange} />
          </span>
          <span className="percentChange">
            <PerformanceColor decimalPlaces={3} isPercentage stat={percentageChange} />
          </span>
        </TickerQuoteFormatter>
      );
    }, [symbol, memoizedQuoteData]);

    if (!props.symbol) {
      return null;
    }

    return renderTicker();
  },
  (prevProps, nextProps) =>
    prevProps.symbol === nextProps.symbol &&
    prevProps.getSnapshotSize === nextProps.getSnapshotSize &&
    prevProps.reflow === nextProps.reflow,
);

export default MiniSnapshotV3;
